/**
 * KB-4771 — the failed half of a save's analytics pair.
 *
 * A save site emits its success event after the save resolves and then keeps
 * going (a re-fetch, a version refresh, a list refresh), any of which can throw
 * into the same outer `catch`. Emitting the failure from that `catch` would
 * record a failure on top of a success. So the failure is reported here, around
 * the save SERVICE call, and the error is rethrown so the site's own `catch` and
 * toast behave exactly as before.
 *
 * The boundary is the service, not the Firestore write: a service that re-reads
 * after writing (`updateCampOrganization`, `updateOffer`) and rejects on that
 * read is reported as a failure. That matches what the user is shown, since the
 * same rejection reaches the site's catch and its "failed" toast.
 *
 * `save` is a thunk rather than a promise so that a save that throws
 * synchronously (or a test double that returns `undefined`) is still caught.
 */
export async function reportSaveRejection<T>(
  save: () => Promise<T>,
  report: (reason: string) => void,
): Promise<T> {
  try {
    return await save();
  } catch (saveError) {
    try {
      report(saveFailureReason(saveError));
    } catch (reportError) {
      console.warn('[analytics] save-failure report failed', reportError);
    }
    throw saveError;
  }
}

/**
 * The save resolved without rejecting but reported that nothing was written (a
 * `null` or `false` result, already toasted by the site or the hook).
 */
export const SAVE_NO_RESULT_REASON = 'no-result';

const ERROR_CODE_SHAPE = /^[a-z0-9][a-z0-9/_-]{0,63}$/i;

/**
 * The error's `code` (Firestore's `permission-denied`, `unavailable`, …) when it
 * has one, else `unknown`. Never the message: messages carry free text, and a
 * validation message can quote what the user typed.
 */
export function saveFailureReason(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' && ERROR_CODE_SHAPE.test(code) ? code : 'unknown';
}
